import { ExportToCsv } from 'export-to-csv-fix-source-map';
import { useMemo, useRef, useState } from 'react';
import BulkActionModal from './BulkActionModal';

const pageSize =
  window.parseInt(process.env.REACT_APP_CSV_EXPORT_PAGE_SIZE) || 100;

const maxResults = 50000;

/**
 * @typedef {(page: number, pageSize: number) => Promise<any[]>} onGetCsvPage
 */

/**
 * @typedef Props
 * @prop {(options: { modalOpen: boolean, onClick: () => void }) => void} children
 * @prop {string} filename
 * @prop {number} rowCount
 * @prop {onGetCsvPage} onGetCsvPage
 */

/**
 * @param {Props} props
 */
export default function ExportToCsvButton({
  children,
  filename,
  onGetCsvPage,
  rowCount,
}) {
  const [showModal, setShowModal] = useState(false);
  const allItems = useRef([]);
  const itemCount = Math.ceil(Math.min(rowCount, maxResults) / pageSize);

  const reset = () => {
    allItems.current = [];
    setShowModal(false);
  };

  const exporter = useMemo(
    () =>
      new ExportToCsv({
        filename: `${filename}-${new Date().toISOString()}`,
        useKeysAsHeaders: true,
        showLabels: true,
      }),
    [filename],
  );

  const onFinish = () => {
    exporter.generateCsv(allItems.current);
    reset();
  };

  const onProcessIndex = async index => {
    // Uncomment this to simulate a delay:
    // await new Promise(resolve => setTimeout(resolve, 1000));
    try {
      const data = await onGetCsvPage(index + 1, pageSize);
      allItems.current = [...allItems.current, ...data];
    } catch (error) {
      window.alert(
        'There was an error while generating the CSV file. Please try again.',
      );
    }
  };

  const onClick = async () => {
    // If there is only one page of results, we should just download it directly
    if (rowCount < pageSize) {
      await onProcessIndex(0);
      onFinish();
      return;
    } else {
      setShowModal(true);
    }
  };

  let description =
    'Your export is being created. This may take a couple of minutes.';

  if (rowCount > maxResults) {
    description += ` Please note that currently only the first ${maxResults.toLocaleString()} results can be exported.`;
  }

  return (
    <>
      {children({ modalOpen: showModal, onClick })}
      <BulkActionModal
        open={showModal}
        title="Exporting to CSV"
        description={description}
        itemCount={itemCount}
        itemNamePlural="pages"
        onCancel={() => {
          reset();
        }}
        onProcessIndex={onProcessIndex}
        onFinish={onFinish}
      />
    </>
  );
}
